import React, { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { EyeOff, History, Pencil, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useConfirm } from '../../hooks/useConfirm';
import NoteMarkdown from '../notes/NoteMarkdown';
import type { PatientAgentContext } from '../../models/patientAgentContext';
import { patientAgentContextService } from '../../services/patientAgentContextService';
import { emitPatientContextChanged } from '../../services/patientAgentContextEvents';
import { usePatientAgentContext } from './usePatientAgentContext';
import { appendToContext, authorName, contextMeta } from './contextPresentation';
import ContextHistorySheet from './ContextHistorySheet';

interface Props {
  patient: { id: number; name: string };
  // Text to add at the end, in the editor (e.g. a note used as context).
  appendText?: string | null;
  className?: string;
}

// "Contexto clínico para Sissi": what the clinical team wants the agent to
// keep in mind about this patient. Shared by every clinician of the clinic
// (D1); never shown to the patient or the patient's coach (D2).
const PatientAgentContextCard: React.FC<Props> = ({ patient, appendText, className }) => {
  const { context, setContext, loading, error, reload } = usePatientAgentContext(patient.id);
  const confirm = useConfirm();
  const fieldId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // The version the editor started from; a save with any other current
  // version is refused with the other person's text.
  const [baseVersion, setBaseVersion] = useState(0);
  const [conflict, setConflict] = useState<PatientAgentContext | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const appliedAppendRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const firstName = patient.name.split(/\s+/)[0] || patient.name;
  const maxLength = context?.maxLength ?? 4000;

  const startEdit = (text: string, version: number) => {
    setDraft(text);
    setBaseVersion(version);
    setConflict(null);
    setSaveError('');
    setEditing(true);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  // A note sent here opens the editor with its text at the end.
  useEffect(() => {
    if (!appendText || !context || appliedAppendRef.current === appendText) return;
    appliedAppendRef.current = appendText;
    startEdit(appendToContext(editing ? draft : context.text, appendText), editing ? baseVersion : context.version);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appendText, context]);

  const save = async (version: number) => {
    const text = draft.trim();
    if (!text && context?.text) {
      const ok = await confirm({
        title: `¿Borrar el contexto clínico de ${patient.name}?`,
        description: 'Sissi dejará de tenerlo en cuenta desde la próxima respuesta. El texto queda en el historial.',
        confirmText: 'Borrar contexto',
        destructive: true,
      });
      if (!ok) return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const result = await patientAgentContextService.save(patient.id, text, version);
      if (!result.ok) {
        setConflict(result.conflict);
        return;
      }
      setContext(result.context);
      setEditing(false);
      setConflict(null);
      toast(text ? 'Contexto clínico guardado' : 'Contexto clínico borrado');
      emitPatientContextChanged(patient.id);
    } catch (err: any) {
      setSaveError(
        err?.response?.code === 'text_too_long'
          ? `El contexto no puede superar los ${maxLength} caracteres.`
          : 'No se pudo guardar el contexto clínico. Inténtalo de nuevo.',
      );
    } finally {
      setSaving(false);
    }
  };

  const useTheirs = () => {
    if (!conflict) return;
    setContext(conflict);
    setEditing(false);
    setConflict(null);
    toast(`Se mantiene la versión de ${authorName(conflict.updatedBy)}`);
  };

  const tooLong = draft.length > maxLength;

  return (
    <section
      aria-labelledby={`${fieldId}-title`}
      className={cn('rounded-lg border border-border bg-card p-5', className)}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 id={`${fieldId}-title`} className="m-0 flex-1 text-base font-semibold">
          Contexto clínico para Sissi AI
        </h3>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-subtle px-2 py-0.5 text-xs font-semibold text-primary">
          <Users className="size-3.5" aria-hidden="true" /> Equipo clínico
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
          <EyeOff className="size-3.5" aria-hidden="true" /> Ni el paciente ni su App Coach lo ven
        </span>
      </div>
      <p className="mt-1 mb-3.5 max-w-[70ch] text-[13px] text-muted-foreground">
        Lo comparte todo el equipo clínico de {firstName}. Sissi AI lo tiene en cuenta en todas las conversaciones
        sobre este paciente. Ni el paciente ni su App Coach lo ven. No escribas nombres ni datos de contacto.
      </p>

      {loading && !context ? (
        <p className="m-0 text-[13.5px] text-muted-foreground">Cargando contexto clínico…</p>
      ) : error && !context ? (
        <div role="alert" className="text-[13.5px] text-muted-foreground">
          <p className="m-0 mb-2">No se pudo cargar el contexto clínico.</p>
          <Button variant="outline" size="sm" onClick={reload}>
            Reintentar
          </Button>
        </div>
      ) : !context ? null : editing ? (
        <div>
          <label htmlFor={fieldId} className="mb-1.5 block text-[13px] font-semibold">
            Texto que Sissi AI tendrá en cuenta
          </label>
          <Textarea
            id={fieldId}
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={8}
            aria-invalid={tooLong || undefined}
            aria-describedby={`${fieldId}-count`}
            className="min-h-[170px] leading-relaxed"
          />
          <div
            id={`${fieldId}-count`}
            className={cn('mt-1 text-right font-mono text-xs', tooLong ? 'text-destructive' : 'text-muted-foreground')}
          >
            {draft.length} / {maxLength}
          </div>
          {tooLong && (
            <p className="m-0 text-[13px] text-destructive">El contexto no puede superar los {maxLength} caracteres.</p>
          )}

          {conflict && (
            <div role="alert" className="my-3 rounded-[10px] border border-destructive/35 bg-destructive/5 px-3.5 py-3 text-[13.5px]">
              <p className="m-0">
                <strong className="text-destructive">Otra persona cambió el contexto mientras editabas.</strong>{' '}
                {authorName(conflict.updatedBy)} guardó la versión {conflict.version}. Tu texto sigue aquí, sin guardar.
              </p>
              <div className="my-2 rounded-lg border border-border bg-card px-2.5 py-2 text-[13px] whitespace-pre-wrap">
                {conflict.text || 'Borró el contexto.'}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={useTheirs} disabled={saving}>
                  Usar su versión
                </Button>
                <Button size="sm" onClick={() => save(conflict.version)} disabled={saving || tooLong}>
                  Guardar la mía encima
                </Button>
              </div>
            </div>
          )}

          {saveError && (
            <p role="alert" className="my-3 text-[13px] text-destructive">
              {saveError}
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => save(baseVersion)} disabled={saving || tooLong || conflict !== null}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : !context.text ? (
        <div className="py-4 text-center text-[13.5px] text-muted-foreground">
          <p className="m-0 mb-1 text-[15px] font-semibold text-foreground">Sin contexto clínico</p>
          <p className="m-0">
            Añade lo que Sissi AI debe saber siempre de este paciente: diagnósticos, medicación, restricciones.
          </p>
          <div className="mt-3 flex justify-center gap-2">
            <Button size="sm" onClick={() => startEdit('', context.version)}>
              <Plus /> Añadir contexto
            </Button>
            {context.version > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
                <History /> Ver historial
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="rounded-[10px] border border-border bg-muted px-3.5 py-3 text-sm leading-relaxed">
            <NoteMarkdown text={context.text} />
          </div>
          <p className="mt-2.5 mb-0 text-[12.5px] text-muted-foreground">{contextMeta(context)}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => startEdit(context.text, context.version)}>
              <Pencil /> Editar
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
              <History /> Ver historial
            </Button>
          </div>
        </div>
      )}

      <ContextHistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        patient={patient}
      />
    </section>
  );
};

export default PatientAgentContextCard;
